import { logSiteEvent } from "./siteEvents";

declare global {
  interface Window {
    dataLayer: Record<string, unknown>[];
  }
}

/**
 * Push an arbitrary event to the GTM dataLayer.
 * Safe to call before GTM has loaded — the snippet in index.html
 * initializes window.dataLayer synchronously.
 */
export const gtmPush = (event: Record<string, unknown>) => {
  if (typeof window === "undefined") return;
  window.dataLayer = window.dataLayer || [];
  window.dataLayer.push(event);
  const { event: name, ...params } = event;
  if (typeof name === "string") logSiteEvent(name, params);
};

/** Push a virtual pageview for SPA route changes. */
export const gtmPageview = (path: string, title?: string) => {
  gtmPush({
    event: "pageview",
    page: {
      path,
      title: title ?? (typeof document !== "undefined" ? document.title : ""),
      location: typeof window !== "undefined" ? window.location.href : "",
    },
  });
};

/** Generic helper for tracking conversions / interactions. */
export const gtmEvent = (
  name: string,
  params: Record<string, unknown> = {},
) => {
  gtmPush({ event: name, ...params });
};

/* ─────────────────────────────────────────────────────────────
   Google Ads retargeting events.
   These only push to the dataLayer — the actual Google Ads
   remarketing / conversion tags are configured inside GTM.
   ───────────────────────────────────────────────────────────── */

const CURRENCY = "USD";

/** Fired for high-intent marketing pages (Restrooms, Events, Pricing, Contact). */
export const trackKeyPageView = (pageName: string) =>
  gtmEvent("key_page_view", { page_name: pageName });

/** Fired whenever any "Check Availability" CTA on the site is clicked. */
export const trackAvailabilityCtaClick = (source: string) =>
  gtmEvent("availability_cta_click", {
    cta_source: source,
    page_path: typeof window !== "undefined" ? window.location.pathname : "",
  });

/** Fired once per session-ish when a valid email address is captured. */
export const trackEmailCollected = (source: string) =>
  gtmEvent("email_collected", {
    form_source: source,
    page_path: typeof window !== "undefined" ? window.location.pathname : "",
  });

/** Fired when a visitor checks availability for a date range. */
export const trackAvailabilityCheck = (params: {
  startDate?: string;
  endDate?: string;
  days?: number;
}) =>
  gtmEvent("availability_check", {
    start_date: params.startDate,
    end_date: params.endDate,
    days: params.days,
  });

/** Fired each time the booking form advances to a new step. */
export const trackBookingStep = (step: number, stepName: string) =>
  gtmEvent("booking_step", { step, step_name: stepName });

/** Fired when the visitor is handed off to Stripe checkout. */
export const trackBeginCheckout = (params: {
  value?: number;
  days?: number;
  region?: string;
}) =>
  gtmEvent("begin_checkout", {
    value: params.value,
    currency: CURRENCY,
    days: params.days,
    region: params.region,
  });

/** Fired when the visitor places a free 48-hour temporary hold (no payment). */
export const trackHoldRequest = (params: {
  value?: number;
  days?: number;
  region?: string;
}) =>
  gtmEvent("hold_request", {
    value: params.value,
    currency: CURRENCY,
    days: params.days,
    region: params.region,
  });

/** Fired once when a payment is confirmed. */
export const trackPurchase = (params: {
  transactionId: string;
  value?: number;
}) =>
  gtmEvent("purchase", {
    transaction_id: params.transactionId,
    value: params.value,
    currency: CURRENCY,
  });

/** Fired on a successful contact form submission. */
export const trackContactSubmit = (source: string) =>
  gtmEvent("contact_submit", { form_source: source });

/** Fired on a successful quote request submission. */
export const trackQuoteRequest = (params: {
  source: string;
  value?: number;
}) =>
  gtmEvent("quote_request", {
    form_source: params.source,
    value: params.value,
    currency: CURRENCY,
  });
